<template>
  <div class="data-table" >
    <div class="wrapper" >
      <el-table
         v-if="show"
         :class="{'hide-first-column': hideFirstColumn}"
         ref="dataTable"
         :data="hierarchy.list"
         :span-method="spanDimensions"
         :border="true"
         :row-style="rowStyle"
         :row-class-name="({row}) => selectableClassStr(row)"
         @row-click="handleSelect"
         @cell-mouse-enter="row => currentRow = row.sortKey"
         @cell-mouse-leave="currentRow = null" >
        <el-table-column
           v-for="i in depth"
           v-if="!hideFirstColumn || i > 1"
           :class-name="'data-table-column-' + i"
           :label-class-name="'data-table-column-' + i"           
           :key="i"
           :render-header="renderHeader"
           label=""
           :sortable="singleColumn"
           prop="data.Group0Caption" />
        <el-table-column
           v-for="(measure, i) in measures"
           :render-header="renderHeader"
           :key="api.measure.uniqueName(measure)"
           :prop="'data.' + api.measure.name(measure)"
           :sortable="singleColumn"
           :label="translate(api.measure.name(measure))" >
          <template slot-scope="{row}" >
            <template v-if="row.data[api.measure.name(measure)] !== 'N/A'" >
              {{ formatters[api.measure.uniqueName(measure)](row.data[api.measure.name(measure)]) }}
            </template>
          </template>
        </el-table-column>
      </el-table>
    </div>
  </div>
</template>

<script>
import DataTableHeader from './DataTableHeader'
import {rgbToHsv, hsvToRgb} from './colors'
import config from './config'
import selectable from 'd3vue/mixins/selectable.js'
import { trans } from './dataFilter'
import customGraphApiAware from './mixins/customGraphApiAware'

export default {
  mixins: [
    selectable,
    customGraphApiAware
  ],
  props: {
    hideFirstColumn: {
      type: Boolean,
      default: true
    },
    data: {
      required: true,
      type: Array
    },
    measures: {
      required: true,
      type: Array
    },
    dimension: {
      type: Object,
      required: true
    },
    formatters: {
      Object,
      required: true
    },
    rootColor: {
      type: Array,
      default: () => config.primaryColor
    },
    leafColor: {
      type: Array,
      default: () => config.secondaryColor
    },
    lightText: {
      type: Array,
      default: () => config.lightText
    },
    darkText: {
      type: Array,
      default: () => config.darkText
    },
    trans: {
      type: [Object, null],
      default: null
    }
  },
  data () {
    return {
      currentRow: null,
      show: true
    }
  },
  watch: {
    measures () {
      this.show = false
      this.$nextTick(() => {
        this.show = true
      })
    }
  },
  computed: {
    rowStyle () {
      return ({row, rowIndex}) => {
        const c = this.gradient[row.data.Group0Level]
        const dark = row.sortKey === this.currentRow || (c.reduce((a, c) => a + c, 0) > 384)
        return {
          'background-color': 'rgb(' + c.join() + ')',
          color: 'rgb(' + (dark ? this.darkText : this.lightText).join() + ')'
        }
      }
    },
    gradient () {
      const root = rgbToHsv(...this.rootColor)
      const leaf = rgbToHsv(...this.leafColor)
      return Array.apply(null, new Array(this.depth)).map((_, i) => {
        return hsvToRgb(...root.map((v, j) => v + (leaf[j] - v) * i / (this.depth - 1)))
      })
    },
    hierarchy () {
      const map = {}
      const roots = []
      this.data.forEach(d => {
        const data = {
          data: d,
          descendants: typeof map[d.Group0UniqueName] === 'undefined' ? [] : map[d.Group0UniqueName].descendants
        }
        map[d.Group0UniqueName] = data
        if (typeof this.api.data.parent(d) !== 'undefined') {
          if (typeof map[this.api.data.parent(d)] === 'undefined') {
            map[this.api.data.parent(d)] = {
              data: {
                Group0UniqueName: this.api.data.parent(d),
                Group0Caption: this.api.dimension.name(this.dimension.value),
                Group0Level: d.Group0Level - 1
              },
              descendants: []
            }
            roots.push(map[this.api.data.parent(d)])
          }
          map[this.api.data.parent(d)].descendants.push(data)
        } else {
          roots.push(data)
        }
      })
      Object.keys(map).forEach(k => {
        map[k].descendant = this.computeDescendant(map[k])
        this.measures.forEach(m => {
          if (typeof map[k].data[this.api.measure.name(m)] === 'undefined') {
            map[k].data[this.api.measure.name(m)] = map[k].descendants.reduce((acc, child) => {
              return acc + child.data[this.api.measure.name(m)]
            }, 0)
            if (m.formatString === 'Percent') {
              map[k].data[this.api.measure.name(m)] = 'N/A'
            }
          }
        })
      })
      this.computeSortKeys(roots)
      const tmp = {
        roots: roots,
        map: map,
        list: Object.keys(map).map(k => map[k]).sort((a, b) => a.sortKey - b.sortKey)
      }
      return tmp
    },
    depth () {
      return this.hierarchy.list.reduce((acc, data) => Math.max(data.data.Group0Level, acc), 0) + 1
    },
    singleColumn () {
      return this.depth - this.hideFirstColumn
    }
  },
  methods: {
    translate (str) {
      return trans(str, this.trans)
    },
    handleMouseEnter (row, column, cell, event) {
    },
    computeSortKeys (descendants, current) {
      current = (current || 0)
      descendants.forEach(c => {
        c.sortKey = current++
        current = this.computeSortKeys(c.descendants, current)
      })
      return current
    },
    computeDescendant (d) {
      return d.descendants.reduce((a, x) => a + this.computeDescendant(x), 1)
    },
    spanDimensions ({row, column, rowIndex, columnIndex}) {
      if (this.hideFirstColumn) {
        columnIndex++
      }
      if (columnIndex >= this.depth) {
        return [1, 1]
      }
      if (columnIndex === row.data.Group0Level) {
        return [row.descendant, 1]
      }
      return [columnIndex >= row.data.Group0Level ? 1 : 0, 1]
    },
    renderHeader (h, {column}) {
      return h(DataTableHeader, {
        props: {
          column: column
        }
      })
    }
  }
}
</script>

<style scoped>
.data-table {
    overflow: auto;
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    padding: 10px;
}
.wrapper {
    position: relative;
}
.data-table >>> table {
    border: collapse;
    margin-bottom: 2px;
}
.data-table >>> tr.selected td {
    border-top: 1px solid red;
    border-bottom: 1px solid red;
    border-right: 1px solid red;
    position: relative;
}
.data-table >>> tr.selected td::before {
    content: "";
    width: 1px;
    height: 100%;
    display: block;
    background-color: white;
    position: absolute;
    z-index: 1;
    top: 0;
    left: -1px;
}
.data-table >>> tr.selected td:first-child {
    border-left: 1px solid red;
}
.data-table >>> tr.selected td:first-child::before {
    display: none;
}
</style>



// WEBPACK FOOTER //
// node_modules/macarons-graphs-front/src/DataTableHierarchical.vue